/**
 * Usage / billing client — Sprint BH.
 *
 * Wraps the three endpoints from os-core Sprint AS:
 *   GET  /usage/current   → quota + period
 *   GET  /usage/events    → recent LLM calls (filterable)
 *   PUT  /usage/quota     → set soft/hard caps (admin.billing)
 */

import { api, type RequestOptions } from "./client";


export interface QuotaDto {
  current_month_usd:        number;
  monthly_soft_usd:         number | null;
  monthly_hard_usd:         number | null;
  period_start:             string;
  period_end:               string;
  over_soft:                boolean;
  over_hard:                boolean;
}


export interface UsageEventDto {
  event_id:      string;
  occurred_at:   string;
  task_type:     string;
  provider:      string;
  model:         string;
  input_tokens:  number;
  output_tokens: number;
  cost_usd:      number;
  latency_ms:    number | null;
  agent_id:      string | null;
  cache_hit:     boolean;
}


export interface QuotaUpdate {
  monthly_soft_usd: number | null;
  monthly_hard_usd: number | null;
}


/** Sprint G2 — linear month-end LLM-spend projection.
 *  Sprint I2 added the smoothed fields. */
export interface UsageForecastDto {
  period_start:                  string;
  period_end:                    string;
  days_elapsed:                  number;
  days_in_period:                number;
  current_month_usd:             number;
  projected_month_usd:           number;
  /** 7-day rolling-median projection; null when
   *  `days_elapsed < 7`. Sprint I2. */
  projected_month_usd_smoothed:  number | null;
  /** "median_7d" when the smoothed projection is non-null,
   *  otherwise "linear". Sprint I2. */
  smoothing_method:              "linear" | "median_7d";
  monthly_soft_usd:              number | null;
  monthly_hard_usd:              number | null;
  projected_over_soft:           boolean;
  projected_over_hard:           boolean;
  /** L-phase — spend-spike detector. `true` when today's
   *  spend bucket lands far above the trailing-week MAD-based
   *  baseline. `anomaly_spike_usd` and `anomaly_baseline_usd`
   *  carry the supporting numbers (today's actual + the
   *  baseline it tripped) so the banner can explain itself
   *  without a second fetch. All three are `false`/`null`
   *  when the detector has insufficient history. */
  anomaly_detected:              boolean;
  anomaly_spike_usd:             number | null;
  anomaly_baseline_usd:          number | null;
}


export const usageApi = {
  current(opts?: RequestOptions): Promise<QuotaDto> {
    return api.get<QuotaDto>("/usage/current", opts);
  },

  /** Sprint G2 — naive linear forecast. See the server-side
   *  docstring for the smoothing caveat. */
  forecast(opts?: RequestOptions): Promise<UsageForecastDto> {
    return api.get<UsageForecastDto>("/usage/forecast", opts);
  },

  events(
    params: { limit?: number; provider?: string } = {},
    opts?: RequestOptions,
  ): Promise<UsageEventDto[]> {
    const q = new URLSearchParams();
    if (params.limit    !== undefined) q.set("limit",    String(params.limit));
    if (params.provider !== undefined) q.set("provider", params.provider);
    const suffix = q.toString() ? `?${q.toString()}` : "";
    return api.get<UsageEventDto[]>(`/usage/events${suffix}`, opts);
  },

  updateQuota(body: QuotaUpdate, opts?: RequestOptions): Promise<QuotaDto> {
    return api.put<QuotaDto>("/usage/quota", body, opts);
  },
};
